import { apiService } from './api.service';
import { PaginatedResponse } from 'types/index';
import { License } from 'types/license';

class LicensesService {
  async list(page: number = 1, limit: number = 100): Promise<PaginatedResponse<License>> {
    const response = await apiService.get<PaginatedResponse<License>>(`/license/GetPageList?page=${page}&pageSize=${limit}`);

    if ((response as any)?.data && (response as any)?.success !== false && (response as any).data?.data && (response as any).data?.pagination) {
      return (response as any).data as PaginatedResponse<License>;
    }
    const maybeItems = (response as any)?.items;
    const maybeTotal = (response as any)?.totalCount;
    if (Array.isArray(maybeItems) && typeof maybeTotal === 'number') {
      const totalPages = Math.max(1, Math.ceil(maybeTotal / limit));
      return {
        data: maybeItems as License[],
        pagination: { page, limit, total: maybeTotal, totalPages },
      };
    }

    const data = (response as any)?.data || response;
    if (Array.isArray(data)) {
      return {
        data: data as License[],
        pagination: { page, limit, total: data.length, totalPages: 1 },
      };
    }

    throw new Error((response as any)?.message || 'Failed to load licenses');
  }

  async getById(id: string): Promise<License> {
    const response = await apiService.get<any>(`/license/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const licenseData =
      payload?.data && typeof payload.data === 'object'
        ? payload.data
        : typeof payload === 'object'
        ? payload
        : null;

    const encryptedId =
      (payload as any)?.encryptedId ??
      (response as any)?.encryptedId ??
      (licenseData as any)?.encryptedId ??
      null;

    if (licenseData) {
      return { ...licenseData, encryptedId } as License;
    }

    return response as unknown as License;
  }

  async getByPublicKey(key: string): Promise<License> {
    const response = await apiService.get<any>(`/license/Encrypted/${encodeURIComponent(key)}`);
    const payload = (response as any)?.data || response;
    if (payload && typeof payload === 'object') {
      if (payload.data) {
        return { ...(payload.data as License), encryptedId: payload.encryptedId } as License;
      }
      return payload as License;
    }
    return response as unknown as License;
  }

  async create(payload: Partial<License>): Promise<License> {
    const response = await apiService.post<License>(`/license`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as License;
    }
    return (response as any) as License;
  }

  async update(id: string, payload: Partial<License>): Promise<License> {
    const response = await apiService.put<License>(`/license/${id}`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as License;
    }
    return (response as any) as License;
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/license/${id}`);
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || 'Delete failed');
    }
  }

  async generate(companyId: string, validUntil: string, maxNbEmployees: number): Promise<{ blob: Blob; filename: string }> {
    const axios = apiService.getInstance();
    const url = `/license/Generate`;
    const response = await axios.post(url, null, {
      params: { companyId, validUntil, maxNbEmployees },
      responseType: 'blob',
    });

    const contentDisposition = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = `license.lic`;
    if (contentDisposition) {
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
      const extracted = decodeURIComponent(match?.[1] || match?.[2] || '');
      if (extracted) filename = extracted;
    }

    return { blob: response.data as Blob, filename };
  }
}

export const licensesService = new LicensesService();
export default licensesService;


